import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { LoadingWrapper } from 'src/domains/layouts/shared/loaders';
import { BannerContainer, BannerButton, BannerText, BannerWrapper, BannerHeader } from './BannerBox.style';
import { BannerBoxState } from './BannerBox.state';
import { InfoIcon } from 'src/domains/layouts/shared/assets/icons/InfoIcon';
import { useCommon } from 'src/domains/common/Common';
import { UsersState } from 'src/domains/players/state/UsersState';
import { IdentityState } from 'src/domains/players/state/IdMission/IdentityState';

export enum BannerTypes {
    SRG = 'srgBanner',
    ID_MISSION = 'idMissionBanner',
}

interface PropsType {
    bannerType: BannerTypes;
}

export const BannerBox = observer('BannerBox', ({ bannerType }: PropsType) => {
    const common = useCommon();
    const identityState = IdentityState.get(common);
    const usersState = UsersState.get(common);
    const [state] = useState<BannerBoxState>(() => new BannerBoxState());

    const handleClick = (): void => {
        switch (bannerType) {
            case BannerTypes.SRG:
                usersState.toggleSrgModal();
                break;
            case BannerTypes.ID_MISSION:
                identityState.openIdMissionPopup();
                break;
        }
    };

    const renderBannerContent = (): React.ReactNode => {
        switch (bannerType) {
            case BannerTypes.SRG:
                return (
                    <>
                        <InfoIcon />
                        <BannerContainer>
                            <BannerHeader>
                                <I18n
                                    langKey='srg-reopen.box.header'
                                    defaultText='Customer Interaction Questionnaire'
                                />
                            </BannerHeader>
                            <BannerText>
                                <I18n
                                    langKey='srg-reopen.box.message'
                                    defaultText='Please complete the remaining questions in order to activate your account.'
                                />
                            </BannerText>
                        </BannerContainer>
                        <BannerButton
                            onClick={handleClick}
                            size='small'
                            dataTest='srg-reopen-button'
                            noWrap={false}
                        >
                            <I18n
                                langKey='srg-reopen.box.button'
                                defaultText='Open Questionnaire'
                            />
                        </BannerButton>
                    </>
                );
            case BannerTypes.ID_MISSION:
                return (
                    <>
                        <BannerContainer data-test='id-mission-banner'>
                            <BannerHeader data-test='id-mission-banner-header'>
                                <I18n
                                    langKey='id-mission.box.header'
                                    defaultText='Identity Verification Required'
                                />
                            </BannerHeader>
                            <BannerText data-test='id-mission-banner-text'>
                                <I18n
                                    langKey='id-mission.box.message'
                                    defaultText='Please verify your identity to continue using your account.'
                                />
                            </BannerText>
                        </BannerContainer>
                        <BannerButton
                            onClick={handleClick}
                            size='small'
                            dataTest='id-mission-button'
                            noWrap={true}
                        >
                            <I18n
                                langKey='id-mission.box.button'
                                defaultText='Verify Now'
                            />
                        </BannerButton>
                    </>
                );
        }
    };

    return (
        <LoadingWrapper computedHeight={state.boxHeight}>
            <BannerWrapper ref={state.setRef}>{renderBannerContent()}</BannerWrapper>
        </LoadingWrapper>
    );
});
